import {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef
} from "react";
import { useLocation } from "react-router-dom";
import CompanyService, { DataType } from "../../services/CompanyService";
import { FilterCollection } from "../../services/FilterService";
import { TranslationService } from "../../services/TranslationService";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import { ButtonTooltipIcon } from "../shared/components/ButtonTooltipIcon";
import Dropdown from "../shared/components/Dropdown";
import Table, { TableHeaderWithFieldId } from "../shared/Table";
import TableContext from "../task/TableContext";
import InboxListContext from "./InboxListContext";
import ActivityService from "../../services/ActivityService";
import { ToastService } from "../shared/bootstrap/Toast";
import { InboxTableItem, getInboxTableHeaders } from "./InboxTableItem";
import { InboxContext, QuickFilterType } from "./InboxTableProvider";
import SearchInput from "../shared/components/SearchInput";
import Collapsable from "../shared/bootstrap/Collapsable";
import TooltipComponent from "../shared/TooltipComponent";
import archiveSrc from "../../assets/img/archive_white_20dp.png";
import unarchiveSrc from "../../assets/img/move_to_inbox_white_20dp.png";
import markReadSrc from "../../assets/img/mark_read_white_20dp.png";
import markSpamSrc from "../../assets/img/hexagon-exclamation.svg";
import { ActivityListResponse } from "../client/activity/entities/AcitivityListResponse";
import { addIf } from "../../utils/Utils";
import CounterMsgContext from "../shared/CounterMsgContext";
import ModalService from "../shared/bootstrap/modal/Modal";

const READ_STATES = {
  ALL: "0",
  READ: "1",
  NOT_READ: "2",
  ONLY_ARCHIVED: "3"
};

export const InboxList = ({ quickfilter }: { quickfilter: number }) => {
  const [readStatus, setReadStatus] = useState<string>(READ_STATES.NOT_READ);
  const prevQuickfilterRef = useRef<number>(quickfilter);
  const { search } = useLocation();
  const filterQS = new URLSearchParams(search).get("filter") ?? "";
  const { translate } = TranslationService;
  const { reload, applySearch } = useContext(TableContext);
  const { setRequest, request, response } = useContext(InboxContext);
  const { getCounterInbox } = useContext(CounterMsgContext);
  const { messageIds, messageAll, setMessageAll, setMessageIds } =
    useContext(InboxListContext);

  // Resetear readStatus cuando cambia el quickfilter
  useEffect(() => {
    if (prevQuickfilterRef.current !== quickfilter) {
      prevQuickfilterRef.current = quickfilter;
      // Resetear al estado por defecto según el tipo
      setReadStatus(
        quickfilter === QuickFilterType.Email
          ? READ_STATES.NOT_READ
          : READ_STATES.NOT_READ
      );
    }
  }, [quickfilter]);

  // Sincronizar readStatus con request cuando cambia desde fuera (por ejemplo, al cambiar de carpeta)
  useEffect(() => {
    // Sincronizar readStatus con request cuando cambia desde fuera
    if (request.archived === true) {
      // En carpeta Archivados
      if (request.read === undefined) {
        setReadStatus(READ_STATES.ALL);
      } else if (request.read === true) {
        setReadStatus(READ_STATES.READ);
      } else {
        setReadStatus(READ_STATES.NOT_READ);
      }
    } else if (request.spam === true) {
      // En carpeta Spam
      if (request.read === undefined) {
        setReadStatus(READ_STATES.ALL);
      } else if (request.read === true) {
        setReadStatus(READ_STATES.READ);
      } else {
        setReadStatus(READ_STATES.NOT_READ);
      }
    } else {
      // En Recibidos
      if (request.read === undefined) {
        setReadStatus(READ_STATES.ALL);
      } else if (request.read === true) {
        setReadStatus(READ_STATES.READ);
      } else {
        setReadStatus(READ_STATES.NOT_READ);
      }
    }
  }, [request.read, request.archived, request.spam]);
  const emailToMessage = (email: ActivityListResponse.Item) => ({
    id: email.ID,
    read: Boolean(email.Read),
    archived: Boolean(email.Archived),
    spam: Boolean(email.Spam)
  });
  const emailIdToMessage = useCallback(
    (id: number) => {
      const email = response?.list.find((x) => x.ID === id);
      if (!email) {
        return undefined;
      }
      return emailToMessage(email);
    },
    [response?.list]
  );
  const responseIds: number[] = useMemo(
    () => (response?.list ? response?.list.map((x) => x.ID) : []),
    [response?.list]
  );

  const messagesIdsSelected = useMemo(() => {
    const messagesSelected: number[] = [];
    messageIds.forEach((message) => {
      if (responseIds.includes(message)) {
        messagesSelected.push(message);
      }
    });
    return messagesSelected;
  }, [messageIds, responseIds]);

  const typeValues = useMemo(() => {
    const values = [
      { Value: translate.WhatsAppAll, Id: READ_STATES.ALL },
      { Value: translate.WhatsAppRead, Id: READ_STATES.READ },
      { Value: translate.WhatsAppNotRead, Id: READ_STATES.NOT_READ }
    ];
    return values;
  }, [translate]);

  const onApplySearch = (keyword: string) => {
    applySearch(keyword);
  };

  const handleExport = async () => {
    let requestToSend = { ...request, inbound: true };

    if (request.quickfilter === QuickFilterType.Email) {
      if (request.spam !== true) {
        requestToSend.spam = false;
      }
      if (request.archived !== true) {
        requestToSend.archived = false;
      }
    } else {
      if (request.archived !== true) {
        requestToSend.archived = false;
      }
    }

    await ActivityService.exportInboxList(
      requestToSend,
      response?.itemCount ?? 0
    );
  };

  const getPagedMessageIds = () =>
    Array.from(document.getElementsByClassName("checkbox-inbox")).reduce<
      number[]
    >((ids, element) => {
      const inputElement = element as HTMLInputElement;
      const invoiceId = inputElement.dataset["invoiceselected"];
      if (invoiceId) ids.push(parseInt(invoiceId));
      return ids;
    }, []);

  const toggleOneCheckbox = (itemId: number, checked: boolean) => {
    setMessageIds(
      checked ? [...messageIds, itemId] : messageIds.filter((x) => x !== itemId)
    );
    setMessageAll(false);
  };

  const tableHeaders = useMemo(() => {
    const toggleAllCheckboxes = (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setMessageIds(checked ? getPagedMessageIds() : []);
      setMessageAll(checked);
    };
    const headers = getInboxTableHeaders(quickfilter);
    headers.unshift(
      new TableHeaderWithFieldId(
        "Checkbox",
        () => (
          <input
            type="checkbox"
            onChange={toggleAllCheckboxes}
            checked={messageAll}
            disabled={!CompanyService.canActOnMessage()}
          />
        ),
        false,
        false,
        "checkbox",
        DataType.IOStatus,
        "w-40px"
      )
    );
    return headers;
  }, [quickfilter, setMessageIds, setMessageAll, messageAll]);

  const getSelectedCount = () =>
    messageAll
      ? (response?.itemCount ?? messagesIdsSelected.length)
      : messagesIdsSelected.length;

  const handleArchiveMessages = () => {
    if (!messagesIdsSelected.length) return;
    const count = getSelectedCount();
    ModalService.showDefaultModal({
      title: translate.Archive,
      message: translate.AreYouSureArchiveMessages.replaceAll(
        "{0}",
        count.toString()
      ),
      acceptButtonLabel: translate.Archive,
      onAcceptClick: async () => {
        const result = await ActivityService.setArchived({
          ids: messagesIdsSelected,
          archived: "1",
          filters: request,
          all: messageAll
        });
        if (result instanceof Error) {
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          await controlCheckActions();
          await getCounterInbox();
          await reload();
        }
      }
    });
  };

  const handleUnarchiveMessages = () => {
    if (!messagesIdsSelected.length) return;
    const count = getSelectedCount();
    ModalService.showDefaultModal({
      title: translate.Unarchive,
      message: translate.AreYouSureUnarchiveMessages.replaceAll(
        "{0}",
        count.toString()
      ),
      acceptButtonLabel: translate.Unarchive,
      onAcceptClick: async () => {
        const result = await ActivityService.setArchived({
          ids: messagesIdsSelected,
          archived: "0",
          filters: request,
          all: messageAll
        });
        if (result instanceof Error) {
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          await controlCheckActions();
          await getCounterInbox();
          await reload();
        }
      }
    });
  };

  const handleReadStatusChange = (read: boolean) => {
    if (!messagesIdsSelected.length) return;
    const count = getSelectedCount();
    const message = read
      ? translate.AreYouSureMarkAsReadMessages.replaceAll(
          "{0}",
          count.toString()
        )
      : translate.AreYouSureMarkAsNotReadMessages.replaceAll(
          "{0}",
          count.toString()
        );
    const title = read ? translate.MarkAsRead : translate.MarkAsNotRead;
    ModalService.showDefaultModal({
      title,
      message,
      acceptButtonLabel: title,
      onAcceptClick: async () => {
        const result = await ActivityService.setRead({
          ids: messagesIdsSelected,
          read: read ? "1" : "0",
          filters: request,
          all: messageAll
        });
        if (result instanceof Error) {
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          await controlCheckActions();
          await getCounterInbox();
          await reload();
        }
      }
    });
  };

  const handleSpamMessages = (spam: boolean) => {
    if (!messagesIdsSelected.length) return;
    const count = getSelectedCount();
    const message = spam
      ? translate.AreYouSureMarkAsSpamMessages.replaceAll(
          "{0}",
          count.toString()
        )
      : translate.AreYouSureMarkAsNotSpamMessages.replaceAll(
          "{0}",
          count.toString()
        );
    const title = spam ? translate.MarkAsSpam : translate.MarkAsNotSpam;
    ModalService.showDefaultModal({
      title,
      message,
      acceptButtonLabel: title,
      onAcceptClick: async () => {
        const result = await ActivityService.setSpam({
          ids: messagesIdsSelected,
          spam: spam ? "1" : "0",
          filters: request,
          all: messageAll
        });
        if (result instanceof Error) {
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          await controlCheckActions();
          await getCounterInbox();
          await reload();
        }
      }
    });
  };

  const handleDeleteMessages = async () => {
    if (messagesIdsSelected.length) {
      ModalService.showDefaultModal({
        title: TranslationService.translate.Delete,
        message: TranslationService.translate.AreYouSureDeleteEmail,
        acceptButtonLabel: TranslationService.translate.Delete,
        acceptButtonClassName: "btn btn-danger",
        onAcceptClick: async () => {
          const result = await ActivityService.deleteEmails({
            ids: messagesIdsSelected,
            filters: request,
            all: messageAll
          });
          if (result instanceof Error) {
            ToastService.showToast(
              TranslationService.translate.ErrorProcessingRequest,
              undefined,
              "danger"
            );
          } else {
            await controlCheckActions();
            await getCounterInbox();
            await reload();
          }
        }
      });
    }
  };

  const controlCheckActions = () => {
    if (messageAll) {
      setMessageAll(false);
      setMessageIds([]);
    }
  };

  const isArchivedFolder = request?.archived === true;
  const isSpamFolder = request?.spam === true;
  const isEmailSection = request?.quickfilter === QuickFilterType.Email;

  const handleGetStatus = (status: string) => {
    setReadStatus(status);

    if (status === READ_STATES.ALL) {
      setRequest((prev) => ({
        ...prev,
        ...(isArchivedFolder && { archived: true }),
        ...(isSpamFolder && { spam: true }),
        read: undefined
      }));
    } else if (status === READ_STATES.ONLY_ARCHIVED) {
      setRequest((prev) => ({ ...prev, read: true, archived: true }));
    } else {
      setRequest((prev) => ({
        ...prev,
        ...(isArchivedFolder && { archived: true }),
        ...(isSpamFolder && { spam: true }),
        read: status === READ_STATES.READ
      }));
    }
  };

  const messageIdWithInfo = messageIds.map(emailIdToMessage).filterFalsey();
  const buttonConfigs = [
    ...addIf(
      !isArchivedFolder &&
        !isSpamFolder &&
        (messageAll || messageIdWithInfo.some((x) => !x.archived)),
      {
        title: translate.Archive,
        onClick: handleArchiveMessages,
        iconClass: archiveIcon
      }
    ),
    ...addIf(
      isArchivedFolder &&
        (messageAll || messageIdWithInfo.some((x) => x.archived)),
      {
        title: translate.Unarchive,
        onClick: handleUnarchiveMessages,
        iconClass: unarchiveIcon
      }
    ),
    ...addIf(messageAll || messageIdWithInfo.some((x) => !x.read), {
      title: translate.MarkAsRead,
      onClick: () => handleReadStatusChange(true),
      iconClass: markReadIcon
    }),
    ...addIf(messageAll || messageIdWithInfo.some((x) => x.read), {
      title: translate.MarkAsNotRead,
      onClick: () => handleReadStatusChange(false),
      iconClass: markUnreadIcon
    }),
    ...addIf(
      isEmailSection &&
        !isArchivedFolder &&
        !isSpamFolder &&
        (messageAll || messageIdWithInfo.some((x) => !x.spam)),
      {
        title: translate.MarkAsSpam,
        onClick: () => handleSpamMessages(true),
        iconClass: markSpamIcon
      }
    ),
    ...addIf(
      isEmailSection &&
        isSpamFolder &&
        (messageAll || messageIdWithInfo.some((x) => x.spam)),
      {
        title: translate.MarkAsNotSpam,
        onClick: () => handleSpamMessages(false),
        iconClass: markNotSpamIcon
      }
    ),
    ...addIf(
      isEmailSection &&
        isSpamFolder &&
        CompanyService.canDo("deletemail") &&
        (messageAll || messageIdWithInfo.some((x) => x.spam)),
      {
        title: translate.DeleteSelectedEmails,
        onClick: () => handleDeleteMessages(),
        iconClass: deleteIcon
      }
    )
  ];

  return (
    <div
      className="d-flex flex-column "
      style={{
        minHeight: 0,
        overflow: "hidden",
        padding: "0 0.25rem",
        width: "100%"
      }}
    >
      <div
        style={{ height: 55, flexShrink: 0 }}
        className="genericHeader justify-content-between py-0 my-0"
      >
        <div className="d-flex col-md-6 align-items-center py-0 my-0">
          {CompanyService.canDo("export") && (
            <div className="col-auto me-2">
              <ButtonTooltipIcon
                title={translate.Export}
                icon="fa-light fa-arrow-down-to-line"
                onClick={handleExport}
              />
            </div>
          )}
          <div className="col-auto">
            <AdvancedFiltersButton
              savedFiltersId={TYPE_REPORT["Inbox"]}
              minimal={true}
            />
          </div>
          <div className="dropdown-wrapper">
            <Dropdown
              quickFilter
              defaultValue={readStatus}
              items={typeValues}
              onChange={handleGetStatus}
              value={readStatus}
              noBorder={true}
            />
          </div>
        </div>
        <SearchInput onSearch={onApplySearch} autosearch={false} />
      </div>
      <div className="pt-0" style={{ flexShrink: 0 }}>
        <AdvancedFilters
          defaultValue={filterQS}
          page={FilterCollection.ReportInbox}
        />
      </div>

      <div style={{ flexShrink: 0 }}>
        <Collapsable show={messagesIdsSelected.length > 0 || messageAll}>
          <div
            className="selectedCollapse mt-3"
            style={{ width: "fit-content" }}
          >
            {buttonConfigs.map((config, index) => (
              <TooltipComponent title={config.title} key={config.title}>
                <button
                  type="button"
                  className={"btn text-secondary"}
                  onClick={config.onClick}
                >
                  {config.iconClass}
                </button>
              </TooltipComponent>
            ))}
          </div>
        </Collapsable>
      </div>

      <Table
        headers={tableHeaders}
        item={InboxTableItem(toggleOneCheckbox, tableHeaders)}
        stickyHeader
        additionalClass={"inboxTable"}
        fillHeight={true}
      />
    </div>
  );
};

const markUnreadIcon = (
  <svg viewBox="0 -960 960 960" focusable="false" className="inboxIcon">
    <path
      d="M168-192q-29.7,0-50.85-21.16T96-264.04V-696.28Q96-726 117.15-747T168-768H553q-2,17-1,35.5t6,36.5H168L480-517l140-81q14,13 37,24t41,16L480-432L168-611v347H792V-558.46q20-4.54 37.5-14.04T864-594v329.77Q864-234 842.5-213T792-192H168Zm0-504v432V-696Zm576,72q-50,0-85-35t-35-85t35-85t85-35t85,35t35,85t-35,85t-85,35Z"
      fill="#0076b6"
    ></path>
  </svg>
);
export const MarkUnreadIcon = ({ style }: { style?: CSSProperties } = {}) => (
  <svg
    viewBox="0 -960 900 800"
    focusable="false"
    className="inboxIcon"
    style={{
      display: "block",
      verticalAlign: "middle",
      ...style
    }}
    height="2px"
    width="20"
  >
    <path
      d="M168-192q-29.7,0-50.85-21.16T96-264.04V-696.28Q96-726 117.15-747T168-768H553q-2,17-1,35.5t6,36.5H168L480-517l140-81q14,13 37,24t41,16L480-432L168-611v347H792V-558.46q20-4.54 37.5-14.04T864-594v329.77Q864-234 842.5-213T792-192H168Zm0-504v432V-696Zm576,72q-50,0-85-35t-35-85t35-85t85-35t85,35t35,85t-35,85t-85,35Z"
      fill="#0076b6"
    ></path>
  </svg>
);
const archiveIcon = (
  <div
    className="inboxIcon maskIcon"
    style={{ WebkitMaskBoxImage: "url(" + archiveSrc + ")" }}
  ></div>
);
const unarchiveIcon = (
  <div
    className="inboxIcon maskIcon"
    style={{ WebkitMaskBoxImage: "url(" + unarchiveSrc + ")" }}
  ></div>
);
const markReadIcon = (
  <div
    className="inboxIcon maskIcon"
    style={{ WebkitMaskBoxImage: "url(" + markReadSrc + ")" }}
  ></div>
);
const markSpamIcon = (
  <div
    className="inboxIcon maskIcon"
    style={{ WebkitMaskBoxImage: "url(" + markSpamSrc + ")" }}
  ></div>
);
const markNotSpamIcon = <i className={`far fa-inbox inboxIcon`}></i>;
const deleteIcon = <i className={`far fa-trash inboxIcon`}></i>;
